import {Button, IconButton} from '@dropbox/dig-components/buttons';
import {List} from '@dropbox/dig-components/list';
import type {ContentRefObject} from '@dropbox/dig-components/menu';
import {Menu} from '@dropbox/dig-components/menu';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Split} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {
  BoldLine,
  DeleteLine,
  ItalicLine,
  RedactLine,
  StrikethroughLine,
  UnderlineLine,
} from '@dropbox/dig-icons/assets';
import {LexicalComposer} from '@lexical/react/LexicalComposer';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {ContentEditable} from '@lexical/react/LexicalContentEditable';
import {LexicalErrorBoundary} from '@lexical/react/LexicalErrorBoundary';
import {HistoryPlugin} from '@lexical/react/LexicalHistoryPlugin';
import {OnChangePlugin} from '@lexical/react/LexicalOnChangePlugin';
import {RichTextPlugin} from '@lexical/react/LexicalRichTextPlugin';
import {mergeRegister} from '@lexical/utils';
import {useQueryClient} from '@tanstack/react-query';
import {employeeAtom} from 'atoms/employee';
import cx from 'classnames';
import type {DocumentRedactionInlineContent} from 'client';
import {DocumentRedactionResponse, DocumentService, EmployeeLdap} from 'client';
import {Avatar} from 'components/DSYS/Avatar';
import {CancelButton} from 'components/DSYS/CancelButton';
import {
  $createRedactionV2ContentNodes,
  $isRedactionV2Node,
  $replaceRedactionV2NodeWithContent,
  createRedactionV2InlineContentFromText,
  getRedactionV2TextFromContent,
  isRedactionV2InlineContent,
  REDACTION_V2_INLINE_CONTENT_KIND,
  RedactionV2InlineContent,
  RedactionV2TextContentNode,
} from 'components/DSYS/editor/nodes/RedactionV2Node';
import styles from 'components/DSYS/editor/plugins/FloatingRedactionEditor.module.css';
import getEditorTheme from 'components/DSYS/editor/themes/EditorTheme';
import {GlobalSearchTypeahead} from 'components/DSYS/GlobalSearchTypeahead';
import {useOutsideClick} from 'hooks/useOutsideClick';
import {t} from 'i18next';
import {useAtomValue} from 'jotai';
import {
  $createParagraphNode,
  $createTextNode,
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isElementNode,
  $isNodeSelection,
  $isRangeSelection,
  $isTextNode,
  type BaseSelection,
  CLEAR_HISTORY_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_LOW,
  CONTROLLED_TEXT_INSERTION_COMMAND,
  CUT_COMMAND,
  DELETE_CHARACTER_COMMAND,
  DELETE_LINE_COMMAND,
  DELETE_WORD_COMMAND,
  DROP_COMMAND,
  type EditorState,
  FORMAT_TEXT_COMMAND,
  INSERT_LINE_BREAK_COMMAND,
  INSERT_PARAGRAPH_COMMAND,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  LexicalEditor,
  LexicalNode,
  PASTE_COMMAND,
  REMOVE_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  SELECTION_INSERT_CLIPBOARD_NODES_COMMAND,
  type SerializedTextNode,
  type TextFormatType,
} from 'lexical';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {createPortal, flushSync} from 'react-dom';
import {getService} from 'utilities';

import {
  REDACTION_V2_COMMAND_PRIORITY,
  redactionV2EntryQueryKey,
  TOGGLE_REDACTION_V2_EDITOR_COMMAND,
  useRedactionV2Context,
  useRedactionV2Entry,
} from './RedactionV2Context';

type ActiveRedactionV2Editor = {
  nodeKey: string;
  redactionId: string;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
const REDACTION_V2_PERSISTENCE_RETRY_COUNT = 20;
const REDACTION_V2_PERSISTENCE_RETRY_DELAY_MS = 500;
const REDACTION_V2_CLIPBOARD_PLACEHOLDER_TEXT = '[redacted]';

const areEmployeeSelectionsEqual = (left: EmployeeLdap[], right: EmployeeLdap[]) => {
  const leftIds = left.map((employee) => employee.user_id).sort();
  const rightIds = right.map((employee) => employee.user_id).sort();
  return leftIds.length === rightIds.length && leftIds.every((id, index) => id === rightIds[index]);
};

const dedupeEmployeesByUserId = (
  employees: Array<EmployeeLdap | null | undefined>
): EmployeeLdap[] => {
  const seen = new Set<string>();
  return employees.filter((employee): employee is EmployeeLdap => {
    if (!employee?.user_id || seen.has(employee.user_id)) {
      return false;
    }
    seen.add(employee.user_id);
    return true;
  });
};

const invalidateRedactionV2Entry = (
  queryClient: ReturnType<typeof useQueryClient>,
  documentId: string | undefined,
  redactionId: string
) =>
  queryClient.invalidateQueries({
    queryKey: redactionV2EntryQueryKey(documentId, redactionId),
  });

type RedactionV2RichTextValue = {
  text: string;
  content: RedactionV2InlineContent;
};

const redactionV2RichTextTheme = getEditorTheme({textSize: 'small'});

const getEditableRedactionV2Content = (
  content: DocumentRedactionInlineContent | null | undefined,
  fallbackText: string
): RedactionV2InlineContent =>
  isRedactionV2InlineContent(content)
    ? content
    : createRedactionV2InlineContentFromText(fallbackText);

const getEditableRedactionV2Value = (
  content: DocumentRedactionInlineContent | null | undefined,
  fallbackText: string
): RedactionV2RichTextValue => {
  const editableContent = getEditableRedactionV2Content(content, fallbackText);
  return {
    content: editableContent,
    text: getRedactionV2TextFromContent(editableContent) || fallbackText,
  };
};

const getRedactionV2ContentKey = (content: DocumentRedactionInlineContent): string =>
  JSON.stringify(content);

export const applySavedRedactionV2RichTextValue = (
  redaction: DocumentRedactionResponse,
  value: RedactionV2RichTextValue
): DocumentRedactionResponse => ({
  ...redaction,
  text: value.text,
  content: value.content,
});

export const shouldHydrateRedactionV2EditorField = ({
  activeRedactionId,
  hydratedRedactionId,
  hasLocalEdits,
}: {
  activeRedactionId: string;
  hydratedRedactionId: string | null;
  hasLocalEdits: boolean;
}): boolean => hydratedRedactionId !== activeRedactionId || !hasLocalEdits;

const createRedactionV2TextContentNode = (
  text: string,
  format = 0,
  detail = 0,
  style = '',
  version = 1
): RedactionV2TextContentNode => ({
  type: 'text',
  text,
  format,
  detail,
  mode: 'normal',
  style,
  version,
});

const getRedactionV2ValueFromEditorState = (editorState: EditorState): RedactionV2RichTextValue =>
  editorState.read(() => {
    const nodes: RedactionV2TextContentNode[] = [];

    const appendTextNodes = (node: LexicalNode) => {
      if ($isTextNode(node)) {
        const text = node.getTextContent();
        if (text.length === 0) {
          return;
        }

        const serializedNode = node.exportJSON() as SerializedTextNode;
        nodes.push(
          createRedactionV2TextContentNode(
            text,
            serializedNode.format,
            serializedNode.detail,
            serializedNode.style,
            serializedNode.version
          )
        );
        return;
      }

      if ($isElementNode(node)) {
        node.getChildren().forEach(appendTextNodes);
      }
    };

    const topLevelChildren = $getRoot().getChildren();
    topLevelChildren.forEach((child, index) => {
      if (index > 0 && nodes.length > 0) {
        nodes.push(createRedactionV2TextContentNode('\n'));
      }
      appendTextNodes(child);
    });

    return {
      content: {
        kind: REDACTION_V2_INLINE_CONTENT_KIND,
        nodes,
      },
      text: nodes.map((node) => node.text).join(''),
    };
  });

const $isGuardedRedactionV2Node = (node: LexicalNode | null | undefined): boolean =>
  $isRedactionV2Node(node);

const $redactionV2NodeBesideCaret = (direction: 'backward' | 'forward'): LexicalNode | null => {
  const selection = $getSelection();
  if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
    return null;
  }

  const anchor = selection.anchor;
  const anchorNode = anchor.getNode();

  if (anchor.type === 'text' && $isTextNode(anchorNode)) {
    if (direction === 'backward') {
      if (anchor.offset > 0) {
        return null;
      }
      const previous = anchorNode.getPreviousSibling();
      return $isGuardedRedactionV2Node(previous) ? previous : null;
    }

    if (anchor.offset < anchorNode.getTextContentSize()) {
      return null;
    }
    const next = anchorNode.getNextSibling();
    return $isGuardedRedactionV2Node(next) ? next : null;
  }

  if (anchor.type === 'element' && $isElementNode(anchorNode)) {
    const child =
      direction === 'backward'
        ? anchor.offset > 0
          ? anchorNode.getChildAtIndex(anchor.offset - 1)
          : null
        : anchorNode.getChildAtIndex(anchor.offset);
    return $isGuardedRedactionV2Node(child) ? child : null;
  }

  return null;
};

const $selectionContainsRedactionV2Node = (
  selection: BaseSelection | null = $getSelection()
): boolean => {
  if ($isNodeSelection(selection)) {
    return selection.getNodes().some((node) => $isGuardedRedactionV2Node(node));
  }
  if ($isRangeSelection(selection)) {
    return selection.getNodes().some((node) => $isGuardedRedactionV2Node(node));
  }
  return false;
};

const $shouldBlockRedactionV2Deletion = (direction?: 'backward' | 'forward'): boolean => {
  if ($selectionContainsRedactionV2Node()) {
    return true;
  }
  if (!direction) {
    return false;
  }
  return $redactionV2NodeBesideCaret(direction) !== null;
};

const $shouldBlockRedactionV2Replacement = (): boolean => $selectionContainsRedactionV2Node();

export const $redactionV2ClipboardNodesContainRedaction = (nodes: LexicalNode[]): boolean =>
  nodes.some((node) => {
    if ($isGuardedRedactionV2Node(node)) {
      return true;
    }
    if ($isElementNode(node)) {
      return $redactionV2ClipboardNodesContainRedaction(node.getChildren());
    }
    return false;
  });

const $sanitizeRedactionV2ClipboardNode = (node: LexicalNode): LexicalNode => {
  if ($isGuardedRedactionV2Node(node)) {
    return $createTextNode(REDACTION_V2_CLIPBOARD_PLACEHOLDER_TEXT);
  }

  if ($isElementNode(node)) {
    node.getChildren().forEach((child) => {
      const sanitizedChild = $sanitizeRedactionV2ClipboardNode(child);
      if (sanitizedChild !== child) {
        child.replace(sanitizedChild);
      }
    });
  }

  return node;
};

export const $sanitizeRedactionV2ClipboardNodes = (nodes: LexicalNode[]): LexicalNode[] =>
  nodes.map($sanitizeRedactionV2ClipboardNode);

export const $insertRedactionV2SafeClipboardNodes = ({
  nodes,
  selection,
}: {
  nodes: LexicalNode[];
  selection: BaseSelection;
}): boolean => {
  if ($selectionContainsRedactionV2Node(selection)) {
    return true;
  }
  if (!$redactionV2ClipboardNodesContainRedaction(nodes)) {
    return false;
  }

  selection.insertNodes($sanitizeRedactionV2ClipboardNodes(nodes));
  return true;
};

export const RedactionV2GuardsPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const {enabled} = useRedactionV2Context();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    return mergeRegister(
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        () => $shouldBlockRedactionV2Deletion('backward'),
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        () => $shouldBlockRedactionV2Deletion('forward'),
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        REMOVE_TEXT_COMMAND,
        () => $shouldBlockRedactionV2Deletion(),
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        CUT_COMMAND,
        () => $selectionContainsRedactionV2Node(),
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        DELETE_CHARACTER_COMMAND,
        (isBackward: boolean) =>
          $shouldBlockRedactionV2Deletion(isBackward ? 'backward' : 'forward'),
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        DELETE_WORD_COMMAND,
        (isBackward: boolean) =>
          $shouldBlockRedactionV2Deletion(isBackward ? 'backward' : 'forward'),
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        DELETE_LINE_COMMAND,
        (isBackward: boolean) =>
          $shouldBlockRedactionV2Deletion(isBackward ? 'backward' : 'forward'),
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        CONTROLLED_TEXT_INSERTION_COMMAND,
        $shouldBlockRedactionV2Replacement,
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        INSERT_LINE_BREAK_COMMAND,
        $shouldBlockRedactionV2Replacement,
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        INSERT_PARAGRAPH_COMMAND,
        $shouldBlockRedactionV2Replacement,
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        PASTE_COMMAND,
        $shouldBlockRedactionV2Replacement,
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        SELECTION_INSERT_CLIPBOARD_NODES_COMMAND,
        $insertRedactionV2SafeClipboardNodes,
        COMMAND_PRIORITY_CRITICAL
      ),
      editor.registerCommand(
        DROP_COMMAND,
        $shouldBlockRedactionV2Replacement,
        COMMAND_PRIORITY_CRITICAL
      )
    );
  }, [editor, enabled]);

  return null;
};

const RedactionV2RichTextEditablePlugin = ({disabled}: {disabled: boolean}) => {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    editor.setEditable(!disabled);
  }, [disabled, editor]);

  return null;
};

const RedactionV2RichTextControls = ({disabled}: {disabled: boolean}) => {
  const [editor] = useLexicalComposerContext();
  const [activeFormats, setActiveFormats] = useState<Record<TextFormatType, boolean>>({
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    code: false,
    highlight: false,
    lowercase: false,
    subscript: false,
    superscript: false,
    uppercase: false,
    capitalize: false,
  });

  const updateActiveFormats = useCallback(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      return;
    }
    setActiveFormats((formats) => ({
      ...formats,
      bold: selection.hasFormat('bold'),
      italic: selection.hasFormat('italic'),
      underline: selection.hasFormat('underline'),
      strikethrough: selection.hasFormat('strikethrough'),
    }));
  }, []);

  useEffect(
    () =>
      mergeRegister(
        editor.registerUpdateListener(({editorState}) => {
          editorState.read(updateActiveFormats);
        }),
        editor.registerCommand(
          SELECTION_CHANGE_COMMAND,
          () => {
            updateActiveFormats();
            return false;
          },
          COMMAND_PRIORITY_LOW
        )
      ),
    [editor, updateActiveFormats]
  );

  const buttons: Array<{
    format: TextFormatType;
    icon: React.ComponentType<React.SVGAttributes<SVGElement>>;
    label: string;
  }> = [
    {format: 'bold', icon: BoldLine, label: t('toggle_bold')},
    {format: 'italic', icon: ItalicLine, label: t('toggle_italic')},
    {format: 'underline', icon: UnderlineLine, label: t('toggle_underline')},
    {format: 'strikethrough', icon: StrikethroughLine, label: t('toggle_strikethrough')},
  ];

  return (
    <Box className={styles.redactionRichTextToolbar} role="toolbar">
      {buttons.map(({format, icon, label}) => (
        <IconButton
          key={format}
          aria-label={label}
          className={cx(styles.redactionRichTextControl, {
            [styles.redactionRichTextControlActive]: activeFormats[format],
          })}
          disabled={disabled}
          onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, format)}
          onMouseDown={(event: React.MouseEvent) => event.preventDefault()}
          size="small"
          variant="borderless"
        >
          <UIIcon src={icon} />
        </IconButton>
      ))}
    </Box>
  );
};

const RedactionV2RichTextInput = ({
  disabled,
  isInvalid,
  value,
  onChange,
}: {
  disabled: boolean;
  isInvalid: boolean;
  value: RedactionV2RichTextValue;
  onChange: (value: RedactionV2RichTextValue) => void;
}) => {
  const initialContentKey = useMemo(() => getRedactionV2ContentKey(value.content), [value.content]);
  const initialConfig = useMemo(
    () => ({
      namespace: `redaction-v2-rich-text-${initialContentKey}`,
      editable: !disabled,
      theme: redactionV2RichTextTheme,
      onError(error: Error) {
        throw error;
      },
      editorState: () => {
        const root = $getRoot();
        root.clear();

        const paragraph = $createParagraphNode();
        const nodes = $createRedactionV2ContentNodes(value.content, value.text);
        if (nodes.length > 0) {
          paragraph.append(...nodes);
        }
        root.append(paragraph);
      },
    }),
    [disabled, initialContentKey, value.content, value.text]
  );

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <Box
        className={cx(styles.redactionRichTextField, {
          [styles.redactionRichTextFieldInvalid]: isInvalid,
        })}
      >
        <RedactionV2RichTextControls disabled={disabled} />
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              aria-label={t('redacted_text')}
              className={styles.redactionRichTextEditable}
              spellCheck
            />
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <RedactionV2RichTextEditablePlugin disabled={disabled} />
        <OnChangePlugin
          ignoreSelectionChange
          onChange={(editorState) => {
            onChange(getRedactionV2ValueFromEditorState(editorState));
          }}
        />
      </Box>
    </LexicalComposer>
  );
};

export const FloatingRedactionV2EditorPlugin = ({collabOwner}: {collabOwner: EmployeeLdap}) => {
  const [editor] = useLexicalComposerContext();
  const {enabled} = useRedactionV2Context();
  const [activeRedaction, setActiveRedaction] = useState<ActiveRedactionV2Editor | null>(null);
  const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(null);
  const anchorRef = useRef<HTMLElement | null>(null);
  const menuContentRef = useRef<ContentRefObject>(null);

  const closeEditor = useCallback(() => {
    setActiveRedaction(null);
    setAnchorElement(null);
    anchorRef.current = null;
  }, []);

  const syncAnchorElement = useCallback(
    (nodeKey: string | null) => {
      if (!nodeKey) {
        anchorRef.current = null;
        setAnchorElement(null);
        return;
      }

      let isValidRedactionNode = false;
      editor.getEditorState().read(() => {
        const node = $getNodeByKey(nodeKey);
        isValidRedactionNode = Boolean(node && $isRedactionV2Node(node));
      });

      if (!isValidRedactionNode) {
        closeEditor();
        return;
      }

      const candidateAnchor = editor.getElementByKey(nodeKey);
      const nextAnchor =
        candidateAnchor instanceof HTMLElement && candidateAnchor.isConnected
          ? candidateAnchor
          : null;
      anchorRef.current = nextAnchor;
      setAnchorElement((current) => (current === nextAnchor ? current : nextAnchor));
    },
    [closeEditor, editor]
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    return editor.registerCommand(
      TOGGLE_REDACTION_V2_EDITOR_COMMAND,
      ({nodeKey, redactionId}) => {
        editor.read(() => {
          const node = $getNodeByKey(nodeKey);
          if (!node || !$isRedactionV2Node(node)) {
            return;
          }

          setActiveRedaction({
            nodeKey,
            redactionId: redactionId ?? node.getRedactionId(),
          });
        });
        return true;
      },
      REDACTION_V2_COMMAND_PRIORITY
    );
  }, [editor, enabled]);

  useEffect(() => {
    if (!activeRedaction?.nodeKey) {
      anchorRef.current = null;
      setAnchorElement(null);
      return;
    }

    let rafId: number | null = null;
    const refreshAnchor = () => {
      if (rafId !== null) {
        return;
      }

      rafId = requestAnimationFrame(() => {
        rafId = null;
        syncAnchorElement(activeRedaction.nodeKey);
        menuContentRef.current?.update();
      });
    };

    refreshAnchor();

    const unregisterUpdate = editor.registerUpdateListener(() => {
      refreshAnchor();
    });

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      unregisterUpdate();
    };
  }, [activeRedaction?.nodeKey, editor, syncAnchorElement]);

  useEffect(() => {
    if (!activeRedaction?.nodeKey || !anchorElement) {
      return;
    }
    menuContentRef.current?.update();
  }, [activeRedaction?.nodeKey, anchorElement]);

  if (!enabled || !activeRedaction || !anchorElement) {
    return null;
  }

  return createPortal(
    <FloatingRedactionV2Editor
      editor={editor}
      activeRedaction={activeRedaction}
      anchorRef={anchorRef}
      menuContentRef={menuContentRef}
      collabOwner={collabOwner}
      closeEditor={closeEditor}
    />,
    document.body
  );
};

const FloatingRedactionV2Editor = ({
  editor,
  activeRedaction,
  anchorRef,
  menuContentRef,
  collabOwner,
  closeEditor,
}: {
  editor: LexicalEditor;
  activeRedaction: ActiveRedactionV2Editor;
  anchorRef: React.RefObject<HTMLElement>;
  menuContentRef: React.RefObject<ContentRefObject>;
  collabOwner: EmployeeLdap;
  closeEditor: () => void;
}) => {
  const {documentId} = useRedactionV2Context();
  const queryClient = useQueryClient();
  const currentEmployee = useAtomValue(employeeAtom);
  const editorRef = useRef<HTMLDivElement | null>(null);
  const {data: redaction, isLoading} = useRedactionV2Entry(activeRedaction.redactionId);
  const [selectedEmployees, setSelectedEmployees] = useState<EmployeeLdap[]>([]);
  const [editedText, setEditedText] = useState('');
  const [editedContent, setEditedContent] = useState<RedactionV2InlineContent>(
    createRedactionV2InlineContentFromText('')
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);
  const hydratedRedactionIdRef = useRef<string | null>(null);
  const hasLocalAccessEditsRef = useRef(false);
  const hasLocalContentEditsRef = useRef(false);

  useOutsideClick(editorRef, (e) => {
    const redactionElement = (e.target as HTMLElement | null)?.closest<HTMLElement>(
      '[data-redaction-v2-id]'
    );
    if (redactionElement?.getAttribute('data-redaction-v2-id') === activeRedaction.redactionId) {
      return;
    }

    closeEditor();
  });

  const originalEmployees = useMemo(
    () =>
      dedupeEmployeesByUserId([
        ...(redaction?.allowed_employees ?? []),
        currentEmployee,
        collabOwner,
      ]),
    [collabOwner, currentEmployee, redaction]
  );
  const originalValue = useMemo(
    () => getEditableRedactionV2Value(redaction?.content, redaction?.text ?? ''),
    [redaction?.content, redaction?.text]
  );
  const originalText = originalValue.text;
  const originalContentKey = useMemo(
    () => getRedactionV2ContentKey(originalValue.content),
    [originalValue.content]
  );
  const editedContentKey = useMemo(() => getRedactionV2ContentKey(editedContent), [editedContent]);
  const hasAccessChanges = Boolean(
    redaction?.can_manage && !areEmployeeSelectionsEqual(originalEmployees, selectedEmployees)
  );
  const hasContentChanges = editedText !== originalText || editedContentKey !== originalContentKey;
  const hasChanges = hasAccessChanges || hasContentChanges;
  const hasValidEditedText = editedText.length > 0 && editedText.length <= 5000;
  const shouldShowTextValidationError = Boolean(redaction && !hasValidEditedText);
  const canSubmit = Boolean(documentId && redaction?.can_view && !isSaving && !isRemoving);
  const protectedEmployeeIds = useMemo(
    () =>
      new Set(
        [currentEmployee?.user_id, collabOwner.user_id].filter((id): id is string => Boolean(id))
      ),
    [collabOwner.user_id, currentEmployee?.user_id]
  );

  useEffect(() => {
    if (!redaction) {
      return;
    }
    if (!redaction.can_view) {
      closeEditor();
      return;
    }

    const activeRedactionId = activeRedaction.redactionId;
    const shouldHydrateAccess = shouldHydrateRedactionV2EditorField({
      activeRedactionId,
      hydratedRedactionId: hydratedRedactionIdRef.current,
      hasLocalEdits: hasLocalAccessEditsRef.current,
    });
    const shouldHydrateContent = shouldHydrateRedactionV2EditorField({
      activeRedactionId,
      hydratedRedactionId: hydratedRedactionIdRef.current,
      hasLocalEdits: hasLocalContentEditsRef.current,
    });

    if (hydratedRedactionIdRef.current !== activeRedactionId) {
      hydratedRedactionIdRef.current = activeRedactionId;
      hasLocalAccessEditsRef.current = false;
      hasLocalContentEditsRef.current = false;
    }

    if (redaction.can_manage && shouldHydrateAccess) {
      setSelectedEmployees(originalEmployees);
    }
    if (shouldHydrateContent) {
      setEditedText(originalText);
      setEditedContent(originalValue.content);
    }
  }, [
    activeRedaction.redactionId,
    closeEditor,
    originalEmployees,
    originalText,
    originalValue.content,
    redaction,
  ]);

  const handleRemoveRedactionItem = (id: string) => {
    if (protectedEmployeeIds.has(id)) {
      return;
    }
    setSelectedEmployees((employees) => {
      const nextEmployees = employees.filter((employee) => employee.user_id !== id);
      if (nextEmployees.length !== employees.length) {
        hasLocalAccessEditsRef.current = true;
      }
      return nextEmployees;
    });
  };

  const handleSave = async () => {
    if (!documentId || !redaction?.can_view || isSaving || !hasValidEditedText) {
      return;
    }

    setIsSaving(true);
    try {
      let updatedRedaction = redaction;
      if (hasAccessChanges) {
        updatedRedaction = await getService(
          DocumentService
        ).updateRedactionAccessApiV1DocumentDocumentIdRedactionsRedactionIdAccessPatch(
          documentId,
          activeRedaction.redactionId,
          {
            allowed_employee_ids: selectedEmployees.map((employee) => employee.user_id),
          }
        );
      }
      if (hasContentChanges) {
        const contentUpdateResponse = await getService(
          DocumentService
        ).updateRedactionContentApiV1DocumentDocumentIdRedactionsRedactionIdContentPatch(
          documentId,
          activeRedaction.redactionId,
          {
            text: editedText,
            content: editedContent,
          }
        );
        // The accepted payload is the best local display source; an immediate resolve refetch can
        // race with persistence and overwrite the placeholder with plaintext content.
        updatedRedaction = applySavedRedactionV2RichTextValue(contentUpdateResponse, {
          text: editedText,
          content: editedContent,
        });

        editor.update(() => {
          const node = $getNodeByKey(activeRedaction.nodeKey);
          if (
            !node ||
            !$isRedactionV2Node(node) ||
            node.getRedactionId() !== activeRedaction.redactionId
          ) {
            return;
          }
          node.setMaskSize(updatedRedaction.mask_size);
        });
      }
      queryClient.setQueryData(
        redactionV2EntryQueryKey(documentId, activeRedaction.redactionId),
        updatedRedaction
      );
      closeEditor();
    } finally {
      setIsSaving(false);
    }
  };

  const finalizeRemovalWithRetry = async () => {
    let lastError: unknown;
    for (let attempt = 0; attempt < REDACTION_V2_PERSISTENCE_RETRY_COUNT; attempt += 1) {
      try {
        return await getService(
          DocumentService
        ).finalizeRedactionRemovalApiV1DocumentDocumentIdRedactionsRedactionIdFinalizeRemovalPost(
          documentId!,
          activeRedaction.redactionId
        );
      } catch (error) {
        lastError = error;
        await delay(REDACTION_V2_PERSISTENCE_RETRY_DELAY_MS);
      }
    }
    throw lastError;
  };

  const handleRemoveRedaction = async () => {
    if (!documentId || !redaction?.can_manage || isRemoving) {
      return;
    }

    setIsRemoving(true);
    let removalRedaction: DocumentRedactionResponse | null = null;
    let didCloseBeforeAnchorRemoval = false;
    try {
      removalRedaction = await getService(
        DocumentService
      ).beginRedactionRemovalApiV1DocumentDocumentIdRedactionsRedactionIdBeginRemovalPost(
        documentId,
        activeRedaction.redactionId
      );
      const restoredContent = removalRedaction.content ?? redaction.content;
      const restoredText =
        removalRedaction.text ?? redaction.text ?? getRedactionV2TextFromContent(restoredContent);
      let didReplace = false;

      // Remove the anchored menu before replacing its trigger node, otherwise Popper can briefly
      // position the still-open popover at the viewport origin.
      didCloseBeforeAnchorRemoval = true;
      flushSync(() => {
        closeEditor();
      });

      editor.update(() => {
        const node = $getNodeByKey(activeRedaction.nodeKey);
        if (
          !node ||
          !$isRedactionV2Node(node) ||
          node.getRedactionId() !== activeRedaction.redactionId
        ) {
          return;
        }

        const restoredNodes = $replaceRedactionV2NodeWithContent(
          node,
          restoredContent,
          restoredText
        );
        if (restoredNodes.length > 0) {
          restoredNodes[0].selectStart();
          didReplace = true;
        }
      });

      if (didReplace) {
        editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
        await finalizeRemovalWithRetry();
        editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined);
      }
      await invalidateRedactionV2Entry(queryClient, documentId, activeRedaction.redactionId);
    } finally {
      if (!didCloseBeforeAnchorRemoval) {
        setIsRemoving(false);
      }
    }
  };

  return (
    <Box ref={editorRef}>
      <Menu.Content
        ref={menuContentRef}
        triggerRef={anchorRef}
        open={true}
        minWidth="326px"
        style={{maxWidth: '326px'}}
      >
        <Menu.Segment>
          <Box
            display="flex"
            alignItems="center"
            paddingTop="4"
            paddingRight="12"
            paddingBottom="4"
            paddingLeft="12"
            style={{gap: '4px'}}
          >
            <Box style={{marginLeft: '-3px'}}>
              <UIIcon src={RedactLine} />
            </Box>
            <Text isBold>{t('redact_text')}</Text>
          </Box>
        </Menu.Segment>
        <Menu.Segment style={{paddingBottom: 0}}>
          <Box className={styles.redactionEditorContent}>
            {redaction?.can_manage && (
              <>
                <Box paddingLeft="12" paddingRight="12">
                  <GlobalSearchTypeahead
                    filter="people"
                    textInputSize="small"
                    placeholderText={t('add_people_or_teams')}
                    selections={selectedEmployees.map((employee) => employee.user_id)}
                    onSelect={(name, type, uid, id) => {
                      if (type !== 'employee') {
                        return;
                      }
                      setSelectedEmployees((employees) => {
                        if (employees.some((employee) => employee.user_id === id)) {
                          return employees;
                        }
                        hasLocalAccessEditsRef.current = true;
                        return [
                          ...employees,
                          {
                            user_id: id,
                            ldap: uid,
                            name,
                          } as EmployeeLdap,
                        ];
                      });
                    }}
                  />
                  <RedactionV2CountText peopleCount={selectedEmployees.length} />
                </Box>
                <Box className={styles.redactionListScrollContainer}>
                  <List spacing="small" isSelectable style={{marginTop: '-6px'}}>
                    {selectedEmployees.map((employee) => (
                      <RedactionV2ListItem
                        key={`redaction-v2-item__${employee.user_id}`}
                        employee={employee}
                        canDelete={!protectedEmployeeIds.has(employee.user_id)}
                        onClick={() => handleRemoveRedactionItem(employee.user_id)}
                      />
                    ))}
                  </List>
                </Box>
              </>
            )}
            <Box
              className={styles.redactionTextEditor}
              paddingTop="12"
              paddingLeft="12"
              paddingRight="12"
              paddingBottom="12"
            >
              <Box className={styles.redactionTextLabel}>
                <Text size="small" isBold>
                  {t('redacted_text')}
                </Text>
              </Box>
              <RedactionV2RichTextInput
                key={`${activeRedaction.redactionId}:${originalContentKey}`}
                value={originalValue}
                onChange={(nextValue) => {
                  hasLocalContentEditsRef.current =
                    nextValue.text !== originalText ||
                    getRedactionV2ContentKey(nextValue.content) !== originalContentKey;
                  setEditedText(nextValue.text);
                  setEditedContent(nextValue.content);
                }}
                isInvalid={shouldShowTextValidationError}
                disabled={!redaction || !canSubmit || isLoading}
              />
              {shouldShowTextValidationError && (
                <Box className={styles.redactionTextError}>
                  <Text size="small" color="faint">
                    {editedText.length === 0
                      ? t('redacted_text_required')
                      : t('redacted_text_too_long')}
                  </Text>
                </Box>
              )}
            </Box>
          </Box>
        </Menu.Segment>
        <Menu.Segment
          className={styles.redactionEditorFooter}
          style={{justifyContent: redaction?.can_manage ? 'space-between' : 'flex-end'}}
        >
          {redaction?.can_manage && (
            <Button
              variant="transparent"
              onClick={handleRemoveRedaction}
              className={styles.removeButton}
              disabled={!canSubmit || isLoading}
            >
              {t('remove')}
            </Button>
          )}
          <Split gap="8" alignX="right">
            <CancelButton onClick={closeEditor}>{t('cancel')}</CancelButton>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={!canSubmit || !hasChanges || !hasValidEditedText || isLoading}
            >
              {t('save')}
            </Button>
          </Split>
        </Menu.Segment>
      </Menu.Content>
    </Box>
  );
};

const RedactionV2CountText = ({peopleCount}: {peopleCount: number}) => {
  if (peopleCount === 0) {
    return null;
  }

  return (
    <Box className={styles.redactionCountText}>
      <Text isBold>
        {t('people_count', {
          count: peopleCount,
        })}
      </Text>
      <Text> {t('may_view_this_redaction')}</Text>
    </Box>
  );
};

const RedactionV2ListItem = ({
  employee,
  canDelete,
  onClick,
}: {
  employee: EmployeeLdap;
  canDelete: boolean;
  onClick: () => void;
}) => {
  const [isHovered, setIsHovered] = useState(false);
  return (
    <List.Item
      key={`redaction-v2-item__${employee.user_id}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={canDelete ? onClick : undefined}
    >
      <List.Accessory style={{width: '32px', height: '32px'}}>
        <Avatar user={employee} />
      </List.Accessory>
      <List.Content>
        <Text>{employee.name}</Text>
      </List.Content>
      {canDelete && (
        <List.Accessory>
          {isHovered && <UIIcon color="var(--dig-color__text__subtle)" src={DeleteLine} />}
        </List.Accessory>
      )}
    </List.Item>
  );
};
